<script setup>
import { computed, ref, onMounted, reactive } from 'vue';
import utils from '../common/utils';
import AddCustomer from './AddCustomer.vue';
import { useAppStore } from '../store/app';
import EditSubscription from './EditSubscription.vue';
import { CREDITCARD_PAYMENT_TYPE_VALUE, EWAY_ONE_OFF_PAYMENT } from '../common/constants';
import { returndate, getPaymentTypeDisplay, onToggleSubscription } from '../common/methods';
import { useRouter } from 'vue-router';
import format from 'date-fns/format';
const props = defineProps({
  subscriptions: {
    type: Array,
    required: true,
  },
  selectable: {
    type: Boolean,
  },
});
const router = useRouter();
const store = useAppStore();
const page = ref(1);
const initCustomerData = ref({
  status: 1,
  subscriptionTypeName: 'ONE_OFF',
  paymentType: 'staff',
  ...store.customerSelected,
});

const editSubObj = ref({
  isNew: true,
  customer: initCustomerData.value,
});
const searchTxt = ref('');
const showEditSub = ref(false);
const product = ref({});

onMounted(() => {
  store.appLoading = true;
  store.fetchAllBetMakersProducts();
  store.appLoading = false;
});

const tableHeaders = computed(() => {
  return props.selectable
    ? [
        {
          title: '',
          key: 'checkbox',
        },
        { title: 'Product', key: 'productId' },
        { title: 'Sub Name', key: 'subscriptionName' },
        { title: 'Sub Type', key: 'subscriptionTypeName', align: 'center' },
        { title: 'Member Since', key: 'subscriptionStartDate' },
        { title: 'Status', key: 'status' },
        { title: 'Free Trial', key: 'hasFreeTrial' },
        { title: 'Next Payment Date', key: 'expirationDate' },
        { title: 'Commercial', key: 'commercial' },
        { title: 'Payment Type', key: 'paymentType' },
        { title: 'Notes', key: 'notes' },
        { title: 'Actions', key: 'actions' },
      ]
    : [
        { title: 'Product', key: 'productId' },
        { title: 'Sub Name', key: 'subscriptionName' },
        { title: 'Sub Type', key: 'subscriptionTypeName', align: 'center' },
        { title: 'Member Since', key: 'subscriptionStartDate' },
        { title: 'Status', key: 'status' },
        { title: 'Free Trial', key: 'hasFreeTrial' },
        { title: 'Next Payment Date', key: 'expirationDate' },
        { title: 'Commercial', key: 'commercial' },
        { title: 'Payment Type', key: 'paymentType' },
        { title: 'Notes', key: 'notes' },
        { title: 'Actions', key: 'actions' },
      ];
});

/**
 * Set customer edit data
 */
const onEditCustomerSubscriptionData = (subscription) => {
  editSubObj.value.customer = { ...subscription, subscriptionNotes: subscription.notes };
  editSubObj.value.isNew = false;
  showEditSub.value = true;
  product.value = store.products.filter((p) => p.id === subscription.productId)[0];
};

const onCacnelorEnableSub = async (sub) => {
  store.appLoading = true;
  const res = await onToggleSubscription(sub);
  store.appLoading = false;
  onEditSubClose(res);
  store.getCustomerSubscriptions({ customer: store.customerSelected });
};

const filteredSubscriptions = computed(() => {
  if (!searchTxt.value) return props.subscriptions;
  return props.subscriptions.filter((subscription) => {
    return (
      subscription.firstName?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      subscription.lastName?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      subscription.email?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      subscription.username?.toLowerCase().includes(searchTxt.value.toLowerCase())
    );
  });
});

const showNewCustomerDialog = ref(false);

const getProductName = (id) => {
  const product = store.products.filter((p) => p.id === id)[0];
  return product?.name ?? '';
};

const onEditSubClose = (data) => {
  msgToUser.success = data?.success;
  msgToUser.message = data?.message;
  showEditSub.value = false;
  msgToUser.show = data?.message ? true : false;
};

const msgToUser = reactive({
  show: false,
  message: '',
  success: false,
});

const onPushToCustomerTransaction = (customer) => {
  router.push(`/transactions/${customer?.customerId}/${customer?.subscriptionId}`);
};
</script>

<template>
  <div>
    <v-card class="mb-2 pa-2">
      <v-card-title class="text-subtitle-1">
        <v-row>
          <v-col cols="3">
            <div>
              <strong>Full Name: </strong>
              {{ store.customerSelected?.firstName }} {{ store.customerSelected?.lastName }}
            </div>
          </v-col>
          <v-col cols="5">
            <div>
              <strong>Email: </strong>
              {{ store.customerSelected?.email }}
            </div>
          </v-col>
          <v-col cols="4">
            <div>
              <strong>Member ID: </strong>
              {{ store.customerSelected?.memberId }}
            </div>
          </v-col>
        </v-row>
      </v-card-title>
    </v-card>
    <v-card class="pa-8">
      <v-card-title>
        <v-row class="mb-2">
          <v-col>
            <v-btn
              class="my-2"
              color="primary"
              prepend-icon="mdi-account-plus"
              @click="
                ((editSubObj.isNew = true), (editSubObj.customer = initCustomerData)),
                  (showEditSub = true) // Set to default
              ">
              New Subscription
            </v-btn>
            <span> Subscriptions ({{ subscriptions.length }}) </span>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="3">
            <v-text-field
              density="compact"
              append-inner-icon="mdi-magnify"
              placeholder="Search by name or email..."
              single-line
              hide-details
              rounded
              v-model="searchTxt"
              variant="outlined"></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :sort-by="[{ key: 'subscriptionStartDate', order: 'desc' }]"
          :headers="tableHeaders"
          :items="filteredSubscriptions"
          :loading="store.appLoading"
          item-value="customerId"
          item-title="email"
          hover>
          <template v-slot:[`item.productId`]="{ item }">
            {{ getProductName(item?.productId) }}
          </template>
          <template v-slot:[`item.paymentType`]="{ item }">
            {{ getPaymentTypeDisplay(item?.paymentType) }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip density="compact" :color="item?.status === 1 ? 'success' : 'red'">
              {{ item?.status === 1 ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template v-slot:[`item.commercial`]="{ item }">
            <v-chip density="compact" :color="item?.commercial === 1 ? 'success' : 'grey-darken-1'">
              {{ item?.commercial === 1 ? 'Yes' : 'No' }}
            </v-chip>
          </template>
          <template v-slot:[`item.subscriptionStartDate`]="{ item }">
            {{ format(new Date(item.subscriptionStartDate), 'HH:mm:ss dd/MM/yyyy') }}
          </template>
          <template v-slot:[`item.expirationDate`]="{ item }">
            {{ returndate({ date: item.expirationDate }) }}
          </template>
          <template v-slot:[`item.hasFreeTrial`]="{ item }">
            <v-chip density="compact" :color="item?.hasFreeTrial ? 'success' : 'grey-darken-1'">
              {{ item?.hasFreeTrial ? 'Applied' : 'N/A' }}
            </v-chip>
          </template>
          <template v-slot:[`item.subscriptionTypeName`]="{ item }">
            <div>{{ item.subscriptionTypeName }}</div>
            <v-chip
              class="mt-2"
              v-if="
                item.subscriptionTypeName == EWAY_ONE_OFF_PAYMENT &&
                item.subscriptionOneOffTransactionId !== '0'
              "
              density="compact"
              color="warning">
              Txn ID: {{ item.subscriptionOneOffTransactionId }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-tooltip text="Edit Subscription" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onEditCustomerSubscriptionData(item)"
                    variant="text"
                    icon="mdi-pencil-outline">
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="View Eway Recurring Transactions" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    :disabled="
                      !(
                        item.paymentType === CREDITCARD_PAYMENT_TYPE_VALUE &&
                        item.subscriptionTypeName === 'RECURRING'
                      )
                    "
                    v-bind="props"
                    @click="onPushToCustomerTransaction(item)"
                    variant="text"
                    icon="mdi-history">
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip
                :text="item.status ? 'Cancel Subscription' : 'Enable Subscription'"
                location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onCacnelorEnableSub(item)"
                    variant="text"
                    :disabled="
                      item.status === 0 && item.paymentType === CREDITCARD_PAYMENT_TYPE_VALUE
                    "
                    :color="item.status ? 'red darken-1' : 'success'"
                    :icon="
                      item.status ? 'mdi-account-cancel-outline' : 'mdi-account-reactivate-outline'
                    ">
                  </v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-pagination
      v-if="!utils.isEmpty(props.customers)"
      v-model="page"
      :length="15"
      :total-visible="7"></v-pagination>
  </div>
  <AddCustomer v-if="showNewCustomerDialog" @on-close="showNewCustomerDialog = false"></AddCustomer>
  <EditSubscription
    v-if="showEditSub"
    @on-close="onEditSubClose"
    :data="editSubObj"
    :callback="store.getCustomerSubscriptions" />
  <v-snackbar
    location="top center"
    v-model="msgToUser.show"
    :timeout="2000"
    :color="msgToUser.success ? 'success' : 'error'">
    {{ msgToUser.message }}
  </v-snackbar>
</template>

<style lang="scss"></style>
